import {ChangeDetectorRef, Component, Input, OnDestroy, OnInit} from '@angular/core';
import {AsyncPipe, CommonModule, NgForOf, NgIf} from "@angular/common";
import {FormControl, FormsModule, ReactiveFormsModule} from "@angular/forms";
import {MatAutocomplete, MatAutocompleteTrigger, MatOption} from "@angular/material/autocomplete";
import {MatError, MatFormField, MatFormFieldModule, MatLabel} from "@angular/material/form-field";
import {MatIcon, MatIconModule} from "@angular/material/icon";
import {MatButtonModule, MatIconButton} from "@angular/material/button";
import {MatInput, MatInputModule} from "@angular/material/input";
import {MatTooltipModule} from "@angular/material/tooltip";
import {catchError, finalize, Observable, of, Subject, takeUntil} from "rxjs";
import {RestFactoryService} from "../../../../../core/services/rest-factory.service";
import {MatCardModule} from "@angular/material/card";
import {MatListModule} from "@angular/material/list";
import {MatDividerModule} from "@angular/material/divider";
import {environment} from "../../../../../../environments/environment";

@Component({
    selector: 'app-destination-relationship-manager',
    standalone: true,
    imports: [
        AsyncPipe,
        CommonModule,
        FormsModule,
        MatAutocomplete,
        MatAutocompleteTrigger,
        MatButtonModule,
        MatCardModule,
        MatDividerModule,
        MatError,
        MatFormField,
        MatFormFieldModule,
        MatIcon,
        MatIconModule,
        MatIconButton,
        MatInput,
        MatInputModule,
        MatLabel,
        MatListModule,
        MatOption,
        MatTooltipModule,
        NgForOf,
        NgIf,
        ReactiveFormsModule,
    ],
    templateUrl: './destination-relationship-manager.component.html',
    styleUrl: './destination-relationship-manager.component.scss'
})
export class DestinationRelationshipManagerComponent implements OnInit, OnDestroy {

    @Input() id_destination: string;
    @Input() dependencyType: string;
    @Input() en_dependencyType: number;
    @Input() itemsList: any[] = [];

    userUiType: number = environment.user_ui_type || 1;
    inputInternalSearchControl = new FormControl('');
    originalParentList: any[] = [];
    filteredParentList: any[] = [];
    selectedParentElement: any = null;
    parentControl = new FormControl(null);
    filteredParents: Observable<any[]>;
    typingTimeout: any;

    destinationTypes: string[] = [];
    activeDestinationTypeFilter: string | null = null;

    private API_SEARCH_PARENTS = "/admin/destination/searchbyrelationship";
    private API_ADD_PARENT = "/admin/destination/dependencyadd";
    private API_DELETE_PARENT = "/admin/destination/dependencyremove";

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _restFactoryService: RestFactoryService,
    ) {
    }

    ngOnInit(): void {
        this.originalParentList = this.itemsList;
        this.filteredParentList = [...this.originalParentList];
        this.extractDestinationTypes();
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    addParentToList(): void {
        const data = {
            "id_dependency": this.selectedParentElement.id_destination,
            "id_destination": this.id_destination,
            "en_dependencytype": this.en_dependencyType
        };
        const payload = this._restFactoryService.createPayloadPaginator(null, null, data);

        this._restFactoryService.httpPostService(this.API_ADD_PARENT, payload)
            .pipe(
                takeUntil(this._unsubscribeAll),
                finalize(() => this._changeDetectorRef.detectChanges()),
                catchError((err) => of(err))
            )
            .subscribe((response) => {
                this.selectedParentElement.id_destinationdependency = response.id_destinationdependency;
                this.selectedParentElement.ds_destinationtype = this.selectedParentElement.ds_en_destinationtype;
                this.originalParentList.push(this.selectedParentElement);
                this.extractDestinationTypes();
                this.searchParentList();
                this.parentControl.reset();
                this.parentControl.markAsPristine();
                this.parentControl.markAsUntouched();
                this._changeDetectorRef.detectChanges();
            });
    }

    onBlur(): void {
        setTimeout(() => {
            const value = this.parentControl.value;
            if (typeof value === 'string' || !value) {
                this.parentControl.reset();
                this.selectedParentElement = null;
            }
        }, 300);
    }

    displayFn(destination: any): string {
        return destination ? destination.ds_name : '';
    }

    optionSelected(event: any): void {
        this.selectedParentElement = event.option.value;
    }

    onKeyupAutoSearch(event: any): void {
        clearTimeout(this.typingTimeout);
        this.typingTimeout = setTimeout(() => {
            if (event.target.value.length > 2) {
                this.searchForParents(event.target.value);
            }
        }, 500);
    }

    searchForParents(searchTerm: string): void {
        const data = {
            "ds_name": searchTerm,
            "id_destination": this.id_destination,
            "en_dependencytype": this.en_dependencyType,
        };
        const payload = this._restFactoryService.createPayloadPaginator(null, null, data);

        this._restFactoryService.httpPostService(this.API_SEARCH_PARENTS, payload, "ar_results")
            .pipe(
                takeUntil(this._unsubscribeAll),
                catchError(() => of([]))
            )
            .subscribe((response: any[]) => {
                const filteredResponse = response.filter(
                    (responseItem) =>
                        !this.originalParentList.some(
                            (originalItem) => originalItem.id_destination === responseItem.id_destination
                        )
                );
                this.filteredParents = of(filteredResponse);
                if (filteredResponse.length === 0) {
                    this.parentControl.setErrors({'notFound': true});
                }
                this._changeDetectorRef.detectChanges();
            });
    }

    searchParentList(): void {
        const searchTerm = this.inputInternalSearchControl.value?.toLowerCase() || '';
        let tempList = [...this.originalParentList];

        if (searchTerm) {
            tempList = tempList.filter(
                (item) => item.ds_label.toLowerCase().includes(searchTerm)
            );
        }

        if (this.activeDestinationTypeFilter) {
            tempList = tempList.filter(
                (item) => item.ds_destinationtype === this.activeDestinationTypeFilter
            );
        }

        this.filteredParentList = tempList;
        this._changeDetectorRef.detectChanges();
    }

    deleteParent(id_parent_destination: string): void {
        const data = {
            id_destinationdependency: id_parent_destination
        };
        const payload = this._restFactoryService.createPayloadPaginator(null, null, data);

        this._restFactoryService.httpPostService(this.API_DELETE_PARENT, payload)
            .pipe(
                takeUntil(this._unsubscribeAll),
                finalize(() => this._changeDetectorRef.detectChanges()),
                catchError((err) => of(err))
            )
            .subscribe(() => {
                const index = this.originalParentList.findIndex(
                    (item) => item.id_destinationdependency === id_parent_destination
                );

                if (index > -1) {
                    this.originalParentList.splice(index, 1);
                }

                this.extractDestinationTypes();
                this.searchParentList();
                this._changeDetectorRef.detectChanges();
            });
    }

    private extractDestinationTypes(): void {
        const types = this.originalParentList.map(item => item.ds_destinationtype);
        this.destinationTypes = [...new Set(types)].sort();
    }

    setDestinationTypeFilter(type: string | null): void {
        this.activeDestinationTypeFilter = type;
        this.searchParentList();
    }
}
